// Redux slice for Agency Skills authoring (used by SkillsHome + SkillEditor).
// Separate from the legacy templates state used by AI managers.

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

export const fetchAgencySkills = createAsyncThunk(
  'agencySkills/fetchList',
  async ({ category, status } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (status) params.set('status', status);
      const qs = params.toString();
      const res = await authFetch(`/.netlify/functions/agency-skills-list${qs ? `?${qs}` : ''}`, { method: 'GET' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchAgencySkillDetail = createAsyncThunk(
  'agencySkills/fetchDetail',
  async (key, { rejectWithValue }) => {
    try {
      const res = await authFetch(`/.netlify/functions/agency-skills-get?key=${encodeURIComponent(key)}`, { method: 'GET' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveAgencySkill = createAsyncThunk(
  'agencySkills/save',
  async (skill, { rejectWithValue }) => {
    try {
      const res = await authFetch('/.netlify/functions/agency-skills-save', {
        method: 'POST',
        body: JSON.stringify(skill),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const publishAgencySkill = createAsyncThunk(
  'agencySkills/publish',
  async (key, { rejectWithValue }) => {
    try {
      const res = await authFetch('/.netlify/functions/agency-skills-publish', {
        method: 'POST',
        body: JSON.stringify({ key }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const archiveAgencySkill = createAsyncThunk(
  'agencySkills/archive',
  async (key, { rejectWithValue }) => {
    try {
      const res = await authFetch('/.netlify/functions/agency-skills-archive', {
        method: 'POST',
        body: JSON.stringify({ key }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const slice = createSlice({
  name: 'agencySkills',
  initialState: {
    skills: [],
    activeSkill: null,
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearActiveSkill: (state) => { state.activeSkill = null; },
    clearAgencySkillsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgencySkills.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAgencySkills.fulfilled, (state, action) => {
        state.loading = false;
        state.skills = action.payload.skills || [];
      })
      .addCase(fetchAgencySkills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch skills';
      })
      .addCase(fetchAgencySkillDetail.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAgencySkillDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.activeSkill = action.payload.skill || null;
      })
      .addCase(fetchAgencySkillDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch skill';
      })
      .addCase(saveAgencySkill.pending, (state) => { state.saving = true; state.error = null; })
      .addCase(saveAgencySkill.fulfilled, (state, action) => {
        state.saving = false;
        state.activeSkill = action.payload.skill || state.activeSkill;
      })
      .addCase(saveAgencySkill.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || 'Failed to save skill';
      })
      .addCase(publishAgencySkill.fulfilled, (state, action) => {
        state.activeSkill = action.payload.skill || state.activeSkill;
      })
      .addCase(archiveAgencySkill.fulfilled, (state, action) => {
        const archived = action.payload.skill;
        if (archived) {
          state.skills = state.skills.filter((s) => s.key !== archived.key);
        }
      });
  },
});

export const { clearActiveSkill, clearAgencySkillsError } = slice.actions;
export const selectAgencySkills = (state) => state.agencySkills?.skills || [];
export const selectActiveSkill = (state) => state.agencySkills?.activeSkill || null;
export const selectAgencySkillsLoading = (state) => state.agencySkills?.loading || false;
export const selectAgencySkillsSaving = (state) => state.agencySkills?.saving || false;
export const selectAgencySkillsError = (state) => state.agencySkills?.error || null;

export default slice.reducer;
